// Delivery reporting shared by SES and Outlook (Graph) sends. Pure, browser-safe.
// SES reports delivery (status 'delivered'). Outlook reports nothing on success: a CRM-sent Graph message
// with no NDR 24 h after sent_at is "Livré (probable)" / "Delivered (probable)" — INFERRED, never stored
// (an NDR arriving later still turns it 'bounced'). Bounces: gavion-graph-sync → apply_graph_ndr.
// Unsubscribes: link (process_unsubscribe) or a reply asking to stop (process_reply_unsubscribe).

import { addBusinessDays } from "./sendWindow.ts";

export const DELIVERED_PROBABLE_MS = 24 * 3_600_000;

export type DeliveryMessage = {
  id: number | string;
  enrollment_id?: number | string | null;
  provider: string;
  direction: string;
  status: string;
  sent_at: string | null;
  /** 'crm' (sent by the CRM) or 'sync' (the rep's own Outlook mail, imported): no inference for 'sync'. */
  origin?: string | null;
};
export type DeliveryEnrollment = { id: number | string; email: string };
export type SequenceDeliveryStats = {
  enrolled: number;
  /** Handed to a provider (sent, delivered, bounced or complained). */
  sent: number;
  /** Confirmed by SES (a complaint implies delivery, as in the campaign report). */
  delivered: number;
  /** Graph, inferred: no NDR within 24 h. */
  delivered_probable: number;
  bounced: number;
  complained: number;
  /** Enrolled addresses that received at least one message and are unsubscribed. */
  unsubscribed: number;
  failed: number;
};
export type DeliveryBadge =
  | "bounced"
  | "complained"
  | "delivered"
  | "delivered_probable";

const LEFT = new Set(["sent", "delivered", "bounced", "complained"]);

export function isDeliveredProbable(
  m: DeliveryMessage,
  now: Date = new Date(),
): boolean {
  if (m.provider !== "graph" || m.direction !== "outbound") return false;
  // NDRs are only matched to CRM sends: a synced manual mail would show "delivered" even after bouncing (M2).
  if ((m.origin ?? "crm") !== "crm") return false;
  if (m.status !== "sent" || !m.sent_at) return false;
  const at = Date.parse(m.sent_at);
  return Number.isFinite(at) && now.getTime() - at >= DELIVERED_PROBABLE_MS;
}

/** Delivery badge of an outbound message on the lead timeline; null when there is nothing to say. */
export function emailDeliveryBadge(
  m: DeliveryMessage,
  now: Date = new Date(),
): DeliveryBadge | null {
  if (m.direction !== "outbound") return null;
  if (m.status === "bounced") return "bounced";
  if (m.status === "complained") return "complained";
  if (m.status === "delivered") return "delivered";
  return isDeliveredProbable(m, now) ? "delivered_probable" : null;
}

export function computeSequenceDeliveryStats(i: {
  enrollments: readonly DeliveryEnrollment[];
  messages: readonly DeliveryMessage[];
  unsubscribedEmails: ReadonlySet<string>;
  now: Date;
}): SequenceDeliveryStats {
  const out = i.messages.filter((m) => m.direction === "outbound");
  const count = (p: (m: DeliveryMessage) => boolean) => out.filter(p).length;
  const reached = new Set(
    out.filter((m) => LEFT.has(m.status)).map((m) => String(m.enrollment_id)),
  );
  const unsubs = new Set(
    [...i.unsubscribedEmails].map((e) => e.trim().toLowerCase()),
  );
  return {
    enrolled: i.enrollments.length,
    sent: count((m) => LEFT.has(m.status)),
    delivered: count(
      (m) => m.status === "delivered" || m.status === "complained",
    ),
    delivered_probable: count((m) => isDeliveredProbable(m, i.now)),
    bounced: count((m) => m.status === "bounced"),
    complained: count((m) => m.status === "complained"),
    unsubscribed: new Set(
      i.enrollments
        .filter((e) => reached.has(String(e.id)))
        .map((e) => e.email.trim().toLowerCase())
        .filter((e) => unsubs.has(e)),
    ).size,
    failed: count((m) => m.status === "failed"),
  };
}

/** CASL: a withdrawal of consent must be honoured within 10 business days; flag pending requests at 5. */
export const STOP_REQUEST_WARN_DAYS = 5;
export const STOP_REQUEST_DEADLINE_DAYS = 10;
const WEEKDAYS = [1, 2, 3, 4, 5] as const;

/**
 * Due dates of a pending stop request (America/Toronto business days, Mon–Fri minus `holidays`):
 * `warnAt` (5 business days, highlighted in the admin list) and `deadline` (10 business days).
 */
export function stopRequestDue(
  createdAt: string,
  holidays: readonly string[] = [],
): { warnAt: Date; deadline: Date } {
  const from = new Date(createdAt);
  return {
    warnAt: addBusinessDays(
      from,
      STOP_REQUEST_WARN_DAYS,
      "00:00",
      WEEKDAYS,
      holidays,
    ),
    deadline: addBusinessDays(
      from,
      STOP_REQUEST_DEADLINE_DAYS,
      "00:00",
      WEEKDAYS,
      holidays,
    ),
  };
}
